import { AlertTriangle } from "lucide-react";
import { colors, semanticColors, radius, spacing, heights, typography, iconSize, iconStroke } from "@/styles/tokens";

export function SummaryStrip({
  brandIssues,
  inStock,
  stockUnknown,
  showOnlyIssues,
  showOnlyInStock,
  onToggleIssues,
  onToggleInStock,
}: {
  brandIssues: number;
  inStock: number;
  stockUnknown: number;
  showOnlyIssues: boolean;
  showOnlyInStock: boolean;
  onToggleIssues: () => void;
  onToggleInStock: () => void;
}) {
  const noIssues = brandIssues === 0;
  return (
    <div
      data-testid="summary-strip"
      className="flex items-center justify-between"
      style={{
        // ⚠️ WRAPS, and the minHeight is the half that makes wrapping work.
        //
        // Without flexWrap this row is display:flex/nowrap holding two groups
        // that set neither flexShrink:0 nor whiteSpace:nowrap, so a squeeze --
        // the analysis panel at 360px and the explanation panel at 420px take
        // 780px off the main column -- shrank both groups until the LABELS
        // broke inside a fixed 36px box, spilling over the table below and
        // surfacing as a page-level horizontal scrollbar. Its own sibling, the
        // "Material list" header row in QuoteDetail, already wraps for exactly
        // this squeeze; this row is now consistent with it.
        //
        // heights.row became a MINIMUM rather than a fixed height: at 36px
        // flat a wrapped second line has nowhere to go and overflows the
        // border it is supposed to sit inside, which is the same spill in a
        // different direction. The row still measures exactly heights.row on
        // the single-line path, since one line plus the vertical padding is
        // shorter than 36px.
        flexWrap: "wrap", rowGap: spacing[8],
        minHeight: heights.row, paddingLeft: spacing[12], paddingRight: spacing[12],
        paddingTop: spacing[6], paddingBottom: spacing[6],
        backgroundColor: semanticColors.panelBg, border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base, marginBottom: spacing[8],
      }}
    >
      <div className="flex items-center" style={{ gap: spacing[20] }}>
        <div
          data-testid="counter-brand-issues"
          className="flex items-center"
          style={{ gap: spacing[6], fontSize: typography.fontSize.sm, color: noIssues ? semanticColors.textPlaceholder : semanticColors.textPrimary }}
        >
          <span
            style={{
              width: heights.dot, height: heights.dot, borderRadius: "50%",
              backgroundColor: noIssues ? semanticColors.textPlaceholder : colors.red600,
              display: "inline-block",
            }}
          />
          {noIssues ? (
            <span>No brand lock issues</span>
          ) : (
            <>
              <span className="tabular-nums" style={{ fontWeight: typography.fontWeight.medium }}>{brandIssues}</span>
              <span style={{ color: semanticColors.textSecondary }}>brand lock issues</span>
            </>
          )}
        </div>
        <div
          data-testid="counter-in-stock"
          className="flex items-center"
          style={{ gap: spacing[6], fontSize: typography.fontSize.sm, color: semanticColors.textPrimary }}
        >
          <span style={{
            width: heights.dot, height: heights.dot, borderRadius: "50%",
            backgroundColor: colors.green600, display: "inline-block",
          }} />
          <span className="tabular-nums" style={{ fontWeight: typography.fontWeight.medium }}>{inStock}</span>
          <span style={{ color: semanticColors.textSecondary }}>lines in stock</span>
        </div>
        {/* The counter beside "lines in stock" that says which 0 that one means.

            ⚠️ GREY, NOT RED OR AMBER. Per the pill scheme red is a violation
            and amber is a warning, and not having looked up a level is
            neither -- it is the same neutral fact OutOfStockPill already
            chose grey for. Colouring it would make a routine reopened quote
            look like it had a problem.

            ⚠️ RENDERS NOTHING AT ZERO -- not dimmed prose, unlike brandIssues
            beside it. A dimmed "All stock known" was measured at within a few
            px of the counter it replaced, so it reclaimed no width on the one
            path where width is scarce and the answer is always 0: a fresh
            quote, where every line carries a level. Absent is the only zero
            state that actually costs nothing. brandIssues keeps its prose
            because ITS zero is reassurance about a real risk; a stock level
            nobody had to look up is not.

            `stockUnknown > 0` rather than `stockUnknown &&`: the second
            renders a literal 0 into the strip when the count is zero. */}
        {stockUnknown > 0 && (
          <div
            data-testid="counter-stock-unknown"
            className="flex items-center"
            style={{ gap: spacing[6], fontSize: typography.fontSize.sm, color: semanticColors.textPrimary }}
          >
            <span data-testid="counter-stock-unknown-dot" style={{
              width: heights.dot, height: heights.dot, borderRadius: "50%",
              backgroundColor: semanticColors.textPlaceholder, display: "inline-block",
            }} />
            <span className="tabular-nums" style={{ fontWeight: typography.fontWeight.medium }}>{stockUnknown}</span>
            <span style={{ color: semanticColors.textSecondary }}>stock unknown</span>
          </div>
        )}
      </div>

      <div className="flex items-center" style={{ gap: spacing[8] }}>
        <ToggleBtn
          testid="toggle-issues"
          active={showOnlyIssues}
          onClick={onToggleIssues}
          icon={<AlertTriangle size={iconSize.sm} strokeWidth={iconStroke.regular} />}
        >
          Show only issues
        </ToggleBtn>
        <ToggleBtn
          testid="toggle-stock"
          active={showOnlyInStock}
          onClick={onToggleInStock}
        >
          Show only in stock
        </ToggleBtn>
      </div>
    </div>
  );
}

function ToggleBtn({
  active,
  onClick,
  children,
  testid,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
  icon?: React.ReactNode;
}) {
  return (
    <button
      data-testid={testid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      className="flex items-center"
      style={{
        gap: spacing[4], height: heights.iconButton, padding: `0 ${spacing[10]}px`,
        fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
        border: `1px solid ${active ? colors.indigo600 : semanticColors.border}`,
        borderRadius: radius.base,
        backgroundColor: active ? colors.indigo600 : colors.white,
        color: active ? semanticColors.onAccent : semanticColors.textSecondary,
        cursor: "pointer",
      }}
    >
      {icon}
      {children}
    </button>
  );
}
